import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { AdminTeam } from "@/types/api.types";

interface Props {
  team: AdminTeam;
  onSave: (data: {
    name?: string;
    formation?: string;
    isLocked?: boolean;
    totalPoints?: number;
  }) => void;
  onClose: () => void;
  isSaving: boolean;
}

const FORMATIONS = ["4-4-2", "4-3-3", "3-5-2", "3-4-3", "4-5-1", "5-3-2", "5-4-1"];

export default function TeamEditModal({ team, onSave, onClose, isSaving }: Props) {
  const [name, setName] = useState(team.name);
  const [formation, setFormation] = useState(team.formation);
  const [isLocked, setIsLocked] = useState(team.isLocked);
  const [totalPoints, setTotalPoints] = useState(team.totalPoints);
  const [errors, setErrors] = useState<{ name?: string; totalPoints?: string }>({});

  useEffect(() => {
    setName(team.name);
    setFormation(team.formation);
    setIsLocked(team.isLocked);
    setTotalPoints(team.totalPoints);
    setErrors({});
  }, [team]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: typeof errors = {};
    if (!name.trim()) newErrors.name = "Team name is required";
    if (totalPoints < 0) newErrors.totalPoints = "Points cannot be negative";
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});
    onSave({
      ...(name !== team.name && { name }),
      ...(formation !== team.formation && { formation }),
      ...(isLocked !== team.isLocked && { isLocked }),
      ...(totalPoints !== team.totalPoints && { totalPoints }),
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-60 p-6">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-bold text-gray-900">Edit Team</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => { setName(e.target.value); if (e.target.value.trim()) setErrors((er) => ({ ...er, name: undefined })); }}
                className={errors.name ? "border-red-400" : ""}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1 font-medium">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Formation
              </label>
              <select
                value={formation}
                onChange={(e) => setFormation(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {FORMATIONS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Points
              </label>
              <Input
                type="number"
                value={totalPoints}
                onChange={(e) => { setTotalPoints(Number(e.target.value)); setErrors((er) => ({ ...er, totalPoints: undefined })); }}
                className={errors.totalPoints ? "border-red-400" : ""}
              />
              {errors.totalPoints && <p className="text-xs text-red-500 mt-1 font-medium">{errors.totalPoints}</p>}
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isLocked"
                checked={isLocked}
                onChange={(e) => setIsLocked(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="isLocked" className="text-sm font-medium text-gray-700">
                Locked (prevent edits)
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
